export type ILocale =
  | "en-US"
  | "en-GB"
  | "en-AU"
  | "en-AT"
  | "en-CH"
  | "en-IE"
  | "en-SG"
  | "en-MY"
  | "es-ES"
  | "en-DE"
  | "fr-FR"
  | "de-DE"
  | "ja-JP"
  | "he-IL"
  | "cz-CZ";

export const countryCallingCodes: Record<ILocale, string> = {
  "en-US": "1", // United States
  "en-GB": "44", // United Kingdom
  "en-AU": "61", // Australia
  "en-AT": "43", // Austria
  "en-CH": "41", // Switzerland
  "en-IE": "353", // Ireland
  "es-ES": "34", // Spain
  "fr-FR": "33", // France
  "en-DE": "49", // English (Germany)
  "de-DE": "49", // Germany
  "ja-JP": "81", // Japan
  "he-IL": "972", // Israel
  "cz-CZ": "420", // Czechia
  "en-MY": "60", // Malaysia
  "en-SG": "65", // Singapore
};

// Maps a detected ISO country code (from `x-user-country`) to its `countryLabels`
// i18n key. Covers every VELYS-supported country (US, ANZ, EMEA, SEA).
export const countryLabelKeys: Record<string, string> = {
  US: "unitedStates",
  GB: "unitedKingdom",
  AU: "australia",
  AT: "austria",
  CH: "switzerland",
  DE: "germany",
  IE: "ireland",
  SG: "singapore",
  MY: "malaysia",
};

export interface LocaleOption {
  value: string;
  name: string;
}

/** Locale served when a visitor has no usable preference of their own. */
export const DEFAULT_LOCALE = "en-US";

/**
 * Every locale this image is *capable* of serving — one entry per message
 * catalog in `src/messages/`, since `loadMessages()` resolves a tag to a file
 * by its language subtag (`de-DE` → `de.json`). Several English regions share
 * `en.json` and differ only in date/number/unit formatting.
 *
 * This is the catalog, NOT the menu. Which of these a given environment
 * actually offers is decided at runtime by AWS AppConfig — see
 * `lib/locales/localeAvailability.ts`. Adding a language therefore means
 * adding it here (plus a `src/messages/<lang>.json` and, ideally, a
 * `LOCALE_FORMATTING` entry), shipping the image, and then enabling it per
 * environment in the AppConfig console. Nothing here is user-visible on its
 * own, so an untested translation can ride along in the image safely.
 */
export const LOCALE_CATALOG: LocaleOption[] = [
  { value: "en-US", name: "English (US)" },
  { value: "en-GB", name: "English (UK)" },
  { value: "en-AU", name: "English (AU)" },
  { value: "en-AT", name: "English (AT)" },
  { value: "en-CH", name: "English (CH)" },
  { value: "en-IE", name: "English (Ireland)" },
  { value: "en-DE", name: "English (Germany)" },
  { value: "en-SG", name: "English (Singapore)" },
  { value: "en-MY", name: "English (Malaysia)" },
  { value: "de-DE", name: "German (Germany)" },
  { value: "fr-FR", name: "French (France)" },
  { value: "it-IT", name: "Italian (Italy)" },
  { value: "es-ES", name: "Spanish (Spain)" },
  // pt.json / zh.json are region-neutral; these tags pick a market. Switch to
  // pt-BR / zh-TW here (and in LOCALE_FORMATTING) if that's the target — the
  // message file that gets loaded is unaffected either way.
  { value: "pt-PT", name: "Portuguese (Portugal)" },
  { value: "zh-CN", name: "Chinese (Simplified)" },
  { value: "ja-JP", name: "Japanese (Japan)" },
  { value: "he-IL", name: "Hebrew (Israel)" },
  { value: "cz-CZ", name: "Czech (Czechia)" },
];

/** Fast membership test over `LOCALE_CATALOG`. */
export const CATALOG_LOCALE_VALUES: ReadonlySet<string> = new Set(
  LOCALE_CATALOG.map((locale) => locale.value),
);

/**
 * The locale set used when AppConfig is unavailable — local `next dev`, a
 * missing `APPCONFIG_*` env var, or a data-plane call that fails before any
 * configuration has been cached.
 *
 * Deliberately the exact list the app hardcoded before AppConfig existed, so
 * "no configuration" degrades to the previously shipped behaviour rather than
 * to every half-translated language at once.
 */
export const FALLBACK_ENABLED_LOCALES: string[] = [
  "en-US",
  "en-GB",
  "en-AU",
  "en-AT",
  "en-CH",
  "en-IE",
  "en-DE",
  "en-SG",
  "en-MY",
  "de-DE",
];
